import { ChevronRight, SlidersHorizontal } from 'lucide-react';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import type { TagOption } from '@/types/api';
import type { PracticeMode } from '@/lib/practice/session-types';

/**
 * The entry point to the custom-session wizard — ROUTE_INVENTORY §2.11 #33.
 *
 * Two ways in, and they are not the same door. "Configure" opens the wizard
 * blank; a domain chip opens it **pre-seeded** with that domain, which is the
 * whole point of the chips — they are a shortcut past step one, not a filter
 * applied here. A chip that merely opened the modal would look identical and
 * cost the user the step it exists to skip.
 *
 * The CTA carries the current mode in its label, because the wizard inherits
 * it and has no mode control of its own.
 */
export function CustomSessionLauncher({
  mode,
  topDomainTags,
  onConfigure,
  onConfigureWithDomain,
}: {
  mode: PracticeMode;
  /** Already sorted and capped by the caller. */
  topDomainTags: TagOption[];
  onConfigure: () => void;
  onConfigureWithDomain: (domain: string) => void;
}) {
  // A phone gets one row into the same configure flow: the five tag chips
  // and the "Configure · Flashcards" button made this the busiest block on
  // the screen, for a shortcut the flow's own first step offers anyway.
  const phone = useMediaQuery('(max-width: 639.98px)');
  if (phone) return <PhoneCustomRow onConfigure={onConfigure} />;
  return (
    <div className='mt-4 rounded-2xl border border-border bg-card/60 p-5 dark:bg-card'>
      {/* Stacked on a phone. Side by side, `flex-1` gave the text a zero
          basis, so the button kept the row and squeezed the description to a
          word per line, with the button laid over the tag chips. */}
      <div className='flex flex-wrap items-center gap-5 max-sm:flex-col max-sm:items-stretch max-sm:gap-4'>
        <div className='min-w-0 flex-1 space-y-4'>
          <div className='flex items-center gap-4'>
            <span className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-muted dark:text-primary'>
              <SlidersHorizontal className='h-5 w-5' />
            </span>
            <div className='min-w-0 flex-1'>
              <div className='text-sm font-medium text-foreground'>
                Custom session
              </div>
              <div className='text-xs text-muted-foreground'>
                Filter by tag, lesson, state, or length.
              </div>
            </div>
          </div>

          {topDomainTags.length > 0 && (
            <div className='flex flex-wrap gap-1.5 max-sm:gap-2'>
              {topDomainTags.map(({ tag, count }) => (
                <button
                  key={tag}
                  type='button'
                  onClick={() => onConfigureWithDomain(tag)}
                  aria-label={`Configure a custom session filtered to ${tag}`}
                  className='inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs text-foreground transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:border-primary dark:hover:bg-primary/30 dark:hover:text-primary max-sm:h-11 max-sm:px-3.5 max-sm:text-sm'
                >
                  <span className='font-medium'>{tag}</span>
                  <span className='text-xs tabular-nums text-muted-foreground'>
                    {count}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type='button'
          onClick={onConfigure}
          className='group inline-flex flex-shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-gray-300 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400/40 focus-visible:ring-offset-2 dark:bg-white/[0.04] dark:hover:bg-white/[0.07] dark:focus-visible:ring-offset-background max-sm:h-11 max-sm:justify-center'
        >
          Configure
          <span className='opacity-80'>·</span>
          <span>{mode === 'flashcard' ? 'Flashcards' : 'Quiz'}</span>
          <span
            aria-hidden
            className='transition-transform group-hover:translate-x-0.5'
          >
            →
          </span>
        </button>
      </div>
    </div>
  );
}

/** The phone's custom-session entry: one row, into the configure flow. */
function PhoneCustomRow({ onConfigure }: { onConfigure: () => void }) {
  return (
    <button
      type='button'
      onClick={onConfigure}
      className='relative isolate mt-3 flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-left shadow-elevation-1 transition-transform duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99] motion-reduce:active:scale-100'
    >
      <span className='dark:tint-brand flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700'>
        <SlidersHorizontal className='h-4 w-4' aria-hidden='true' />
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block text-base font-medium text-foreground'>
          Custom session
        </span>
        <span className='block truncate text-sm text-muted-foreground'>
          Pick tags, lessons, state and length
        </span>
      </span>
      <ChevronRight
        className='h-5 w-5 flex-shrink-0 text-muted-foreground'
        aria-hidden='true'
      />
    </button>
  );
}
